import { Layout } from "antd";
import classNames from "classnames";
import EmailVerification from "components/EmailVerification/EmailVerification";
import ErrorBoundary from "components/ErrorBoundary/ErrorBoundary";
import PublicHeader from "components/Header/PublicHeader";
import RestrictedSmartBEA from "components/RestrictedAccess/RestrictedSmartBEA";
import { useFeatureFlag } from "hooks/use-feature-flag";
import { useNewRelicBrowserAgent } from "hooks/use-nr-browser-agent";
import useUserSmartBEAStatus from "hooks/useUserSmartBEAStatus";
import ComfortPlus from "pages/ComfortPlus/ComfortPlus";
import SmartBEA from "pages/SmartBEA/SmartBEA";
import React, { useEffect } from "react";
import { Route, Switch, useRouteMatch } from "react-router-dom";

export interface IPublicRouteContext {
  setPublicHeaderShown?: (value: boolean) => void;
  setIsOnboarding?: (value: boolean) => void;
  isOnboarding?: boolean;
  setLayoutClassNames?: (classNames: Record<string, boolean>) => void;
}

export interface PublicRoutesProps {
  initialRoute?: string;
}

const PublicRouteContext = React.createContext<IPublicRouteContext>({});
export const usePublicRouteContext = () => React.useContext(PublicRouteContext);

const PublicRoutes: React.FC<PublicRoutesProps> = ({ initialRoute }) => {
  const [publicHeaderShown, setPublicHeaderShown] = React.useState<boolean>(true);
  const [layoutClassNames, setLayoutClassNames] = React.useState<Record<string, boolean>>({});
  const [isOnboarding, setIsOnboarding] = React.useState<boolean>(false);
  const [userEmail, setUserEmail] = React.useState<string>("");
  const { isRestricted } = useUserSmartBEAStatus();
  const { publicSmartBEAAccessEnabled } = useFeatureFlag();

  const { Content } = Layout;
  const { path } = useRouteMatch();
  const nrBrowserAgent = useNewRelicBrowserAgent();

  // Handle initial route if specified
  useEffect(() => {
    if (initialRoute === "email-verification") {
      // Get email from localStorage if available
      const storedEmail = localStorage.getItem("lastUsedEmail");
      if (storedEmail) {
        setUserEmail(storedEmail);
      }
    }
  }, [initialRoute]);

  const publicRouteCtxValue = React.useMemo(
    () => ({
      setPublicHeaderShown,
      setIsOnboarding,
      isOnboarding,
      setLayoutClassNames,
    }),
    [setIsOnboarding, isOnboarding, setPublicHeaderShown, setLayoutClassNames]
  );

  // Render a component that explains SmartBEA access is disabled
  const SmartBEADisabled = () => (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h2>SmartBEA Public Access Disabled</h2>
      <p>Public access to SmartBEA is currently disabled. Please contact your administrator for more information.</p>
    </div>
  );

  return (
    <Layout className={classNames("layout comfort-plus-layout", layoutClassNames)}>
      <ErrorBoundary newrelicAgent={nrBrowserAgent}>
        <PublicRouteContext.Provider value={publicRouteCtxValue}>
          {publicHeaderShown && <PublicHeader />}
          <Content>
            <Switch>
              {initialRoute === "email-verification" && (
                <Route
                  path="/"
                  render={() => (
                    <div
                      className="email-verification-container"
                      style={{ maxWidth: "1000px", margin: "0 auto", padding: "20px" }}
                    >
                      <EmailVerification
                        email={userEmail}
                        onReturnToLogin={() => {
                          // Clear the URL parameters and redirect to login
                          window.location.href = window.location.origin;
                        }}
                      />
                    </div>
                  )}
                />
              )}
              <Route path={`${path}/comfortplus`} render={() => <ComfortPlus />} />
              <Route
                path={`${path}/smartbea`}
                render={() => {
                  if (!publicSmartBEAAccessEnabled) {
                    return <SmartBEADisabled />;
                  }
                  return isRestricted ? <RestrictedSmartBEA /> : <SmartBEA />;
                }}
              />
            </Switch>
          </Content>
        </PublicRouteContext.Provider>
      </ErrorBoundary>
    </Layout>
  );
};

export default PublicRoutes;
